import React, { useMemo, useRef, useEffect } from 'react';
import MapGL, { Marker, ViewStateChangeEvent } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Layout, Space } from 'antd';
import { BoxyLogoSmall, PickupsIcon } from '../Icons';
import { isValidLatLng } from '@/utils';
import type { MapRef } from 'react-map-gl';

const MAPBOX_TOKEN =
  'pk.eyJ1IjoiaGVzdDEyMyIsImEiOiJja28wZWhscjUwZXpvMm9tdnV0NDhtYjM1In0.UuZF95SWdyCnerb0xORO6A';

interface Location {
  latitude: number;
  longitude: number;
}

interface MapComponentProps {
  targetLocation?: Location;
  driverLocation?: Location;
}

/** Calculate the center and zoom to fit all points with padding */
function getBoundsForLocations(locations: Location[]): {
  latitude: number;
  longitude: number;
  zoom: number;
} {
  if (locations.length === 0) return { latitude: 33.3, longitude: 44.3, zoom: 10 };
  if (locations.length === 1) return { ...locations[0], zoom: 14 };

  const lats = locations.map((l) => l.latitude);
  const lngs = locations.map((l) => l.longitude);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);

  const centerLat = (minLat + maxLat) / 2;
  const centerLng = (minLng + maxLng) / 2;

  // Calculate zoom to fit both points with padding
  const latDiff = Math.abs(maxLat - minLat);
  const lngDiff = Math.abs(maxLng - minLng);
  const maxDiff = Math.max(latDiff, lngDiff);

  // Approximate zoom: smaller diff = higher zoom
  let zoom = 14;
  if (maxDiff > 0.001) zoom = 13;
  if (maxDiff > 0.01) zoom = 12;
  if (maxDiff > 0.05) zoom = 11;
  if (maxDiff > 0.1) zoom = 10;
  if (maxDiff > 0.5) zoom = 8;
  if (maxDiff > 1) zoom = 7;
  if (maxDiff > 5) zoom = 5;

  return { latitude: centerLat, longitude: centerLng, zoom };
}

const MapComponent = ({ driverLocation, targetLocation }: MapComponentProps) => {
  const mapRef = useRef<MapRef>(null);

  // Collect all valid locations
  const validLocations = useMemo(() => {
    const locs: Location[] = [];
    if (targetLocation && isValidLatLng(targetLocation.latitude, targetLocation.longitude)) {
      locs.push(targetLocation);
    }
    if (driverLocation && isValidLatLng(driverLocation.latitude, driverLocation.longitude)) {
      locs.push(driverLocation);
    }
    return locs;
  }, [targetLocation, driverLocation]);

  const initialView = useMemo(() => getBoundsForLocations(validLocations), [validLocations]);

  // After map loads, fit bounds if multiple points
  useEffect(() => {
    if (validLocations.length >= 2 && mapRef.current) {
      const map = mapRef.current.getMap();
      const lats = validLocations.map((l) => l.latitude);
      const lngs = validLocations.map((l) => l.longitude);
      const sw: [number, number] = [Math.min(...lngs), Math.min(...lats)];
      const ne: [number, number] = [Math.max(...lngs), Math.max(...lats)];

      map.fitBounds([sw, ne], { padding: 50, maxZoom: 15, duration: 500 });
    }
  }, [validLocations]);

  return (
    <Layout style={{ height: '100%', width: '100%' }}>
      <MapGL
        ref={mapRef}
        initialViewState={initialView}
        style={{ borderRadius: '10px' }}
        mapStyle='mapbox://styles/mapbox/streets-v11'
        mapboxAccessToken={MAPBOX_TOKEN}
      >
        {targetLocation && isValidLatLng(targetLocation.latitude, targetLocation.longitude) && (
          <Marker latitude={targetLocation.latitude} longitude={targetLocation.longitude}>
            <Space className='bg-customYellowPrimary rounded-full w-7 h-7 flex items-center justify-center'>
              <BoxyLogoSmall className='mt-1' />
            </Space>
          </Marker>
        )}
        {driverLocation && isValidLatLng(driverLocation.latitude, driverLocation.longitude) && (
          <Marker latitude={driverLocation.latitude} longitude={driverLocation.longitude}>
            <Space className='bg-customYellowPrimary rounded-full w-10 h-10 flex items-center justify-center'>
              <PickupsIcon className='mt-2' />
            </Space>
          </Marker>
        )}
      </MapGL>
    </Layout>
  );
};

export default MapComponent;
